import React from 'react'

import styled from 'styled-components/native'

import Assignments from '../../../assignments/Assignments'
import ClientNotes from '../../../clientNotes/ClientNotes'
import ClientProgress from '../../../clientProgress/ClientProgress'
import Medications from '../../../medications/Medications'
import Sessions from '../../../sessions/Sessions'
import BenefitsSummaryContainer from '../redesigned/BenefitsSummaryContainer'

const ComponentsContainer = styled.View({
  display: 'none',
})

type ProviderEpisodeInfo = { text: string; id: string }

type RecordsPdfSourceComponentsProps = {
  medicationsEnabled: boolean
  pdfContentEndDate?: string
  pdfContentStartDate?: string
  providerEpisodesInfoPDF: ProviderEpisodeInfo[]
  setClientAssignmentsPDFContent: (content: any) => void
  setClientProgressPDFContent: (content: any) => void
  setClientSummaryPDFContent: (content: any) => void
  setMedicationsPDFContent: (content: any) => void
  setNotesPDFContent: React.Dispatch<React.SetStateAction<{ [key: string]: React.ReactNode | undefined } | undefined>>
  setSessionsPDFContent: (content: any) => void
}

/**
 * Mounts the full client record screens offscreen so their effects can hand PDF content back up.
 *
 * These are the real feature screens, so rendering them is expensive. `display: none` skips paint
 * but not React reconciliation, so this is memoized to keep unrelated state changes in
 * ClientRecords (typing in the redaction term field, toggling checkboxes) from re-rendering them.
 * Every prop here must stay referentially stable for that to hold.
 */
const RecordsPdfSourceComponentsComponent: React.FC<RecordsPdfSourceComponentsProps> = ({
  medicationsEnabled,
  pdfContentEndDate,
  pdfContentStartDate,
  providerEpisodesInfoPDF,
  setClientAssignmentsPDFContent,
  setClientProgressPDFContent,
  setClientSummaryPDFContent,
  setMedicationsPDFContent,
  setNotesPDFContent,
  setSessionsPDFContent,
}) => (
  <ComponentsContainer>
    <BenefitsSummaryContainer setClientSummaryPDFContent={setClientSummaryPDFContent} />
    <Sessions
      setSessionsPDFContent={setSessionsPDFContent}
      providerEpisodesInfoPDF={providerEpisodesInfoPDF}
      pdfContentStartDate={pdfContentStartDate}
      pdfContentEndDate={pdfContentEndDate}
    />
    {providerEpisodesInfoPDF.map(({ id, text }) => (
      <ClientNotes
        key={id}
        setNotesPDFContent={(notesPDFContent: any) =>
          setNotesPDFContent((prev) => (prev ? { ...prev, [text]: notesPDFContent } : { [text]: notesPDFContent }))
        }
        pdfNotesEpisodeId={id}
        pdfContentStartDate={pdfContentStartDate}
        pdfContentEndDate={pdfContentEndDate}
      />
    ))}
    {/* @ts-expect-error TS(2739) */}
    <ClientProgress
      setClientProgressPDFContent={setClientProgressPDFContent}
      pdfContentStartDate={pdfContentStartDate}
      pdfContentEndDate={pdfContentEndDate}
      providerEpisodesInfo={providerEpisodesInfoPDF}
    />
    <Assignments
      setClientAssignmentsPDFContent={setClientAssignmentsPDFContent}
      pdfContentStartDate={pdfContentStartDate}
      pdfContentEndDate={pdfContentEndDate}
    />
    {medicationsEnabled && <Medications setMedicationsPDFContent={setMedicationsPDFContent} />}
  </ComponentsContainer>
)

RecordsPdfSourceComponentsComponent.displayName = 'RecordsPdfSourceComponents'

export const RecordsPdfSourceComponents = React.memo(RecordsPdfSourceComponentsComponent)
